import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import type { InformationHeroBanner as Value } from "@reactivated";

import { originalUrl } from "../../utils/wagtail";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/hero_banners/hero_banner.html`` (#33285).
 *
 * - ``id="hero-whypp"`` only when ``presentation == 'robust'``.
 * - The content branches on ``presentation == 'normal'`` with an ``else``
 *   (so the robust markup is also the fallback for a null presentation),
 *   exactly like the legacy ``{% if %}/{% else %}``.
 * - The robust logo's ``alt="Posturpedic Technology"`` typo is legacy
 *   verbatim.
 */
export const InformationHeroBanner = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const value = props.value;
    const robust = value.presentation === "robust";
    return (
        <section className="hero" id={robust ? "hero-whypp" : undefined}>
            <picture>
                <source
                    srcSet={originalUrl(value.mobile_image)}
                    media="(max-width: 768px)"
                />
                <img
                    className="hero__background"
                    srcSet={originalUrl(value.image)}
                    alt={value.image?.default_alt_text ?? ""}
                />
            </picture>

            <div className="hero__content container">
                {value.presentation === "normal" ? (
                    <>
                        <h1 className="hero__headline">{value.title}</h1>
                        <p className="hero__copy alt-copy">{value.subtitle}</p>
                    </>
                ) : (
                    <>
                        {value.logo && (
                            <img
                                className="hero__logo hero__logo--small"
                                src={`${staticURL}img/hero/logo-posturepedic.svg`}
                                alt="Posturpedic Technology"
                            />
                        )}
                        <h3 className="hero__headline hero__headline--inverse">
                            {value.title}
                        </h3>
                        <p className="hero__copy hero__copy--inverse alt-copy">
                            {value.subtitle}
                        </p>
                    </>
                )}
            </div>
        </section>
    );
};
